import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  automationCommandLifecycleResponseSchema,
  automationCommandListSchema,
  automationCommandSchema,
  automationButtonListSchema,
  automationButtonRunBodySchema,
  automationButtonRunReceiptSchema,
  automationCommandLogResponseSchema,
  createAutomationCommandBodySchema,
  duplicateAutomationCommandBodySchema,
  duplicateAutomationCommandResponseSchema,
  automationAdminDisableBodySchema,
  automationAdminDisableResponseSchema,
  automationAdminReviewResponseSchema,
  updateAutomationDefinitionBodySchema,
  updateAutomationEnabledBodySchema,
  workspaceAutomationActivityResponseSchema,
  workspaceAutomationUsageResponseSchema,
  type ApiError,
  type AutomationButtonList,
  type AutomationButtonRunBody,
  type AutomationButtonRunReceipt,
  type AutomationCommand,
  type AutomationCommandKind,
  type AutomationCommandLogResponse,
  type AutomationCommandLifecycleResponse,
  type AutomationCommandList,
  type AutomationAdminDisableBody,
  type AutomationAdminDisableResponse,
  type AutomationAdminReviewResponse,
  type AutomationRunOutcome,
  type CreateAutomationCommandBody,
  type DuplicateAutomationCommandBody,
  type DuplicateAutomationCommandResponse,
  type UpdateAutomationDefinitionBody,
  type UpdateAutomationEnabledBody,
  type WorkspaceAutomationActivityResponse,
  type WorkspaceAutomationUsageResponse,
} from '@molt/shared';
import { announce } from './announce';
import { del, emptyResponseSchema, get, patch, post } from './api';
import { useInterfaceLanguage } from './interface-language';
import { queryKeys } from './query-keys';

export function getBoardAutomations(
  boardId: string,
  commandKind?: AutomationCommandKind,
): Promise<AutomationCommandList> {
  const query = commandKind === undefined ? '' : `?commandKind=${commandKind}`;
  return get(
    `/boards/${encodeURIComponent(boardId)}/automations${query}`,
    automationCommandListSchema,
  );
}

export function getAutomationCommand(commandId: string): Promise<AutomationCommand> {
  return get(`/automations/${encodeURIComponent(commandId)}`, automationCommandSchema);
}

/**
 * Read one command's run log — page one, or an **older** page by cursor
 * (`FE-70.2`, `FR-AUTO-013`).
 *
 * ⚠ **`cursor` is additive and optional, and the query string is appended only
 * when one is given.** Page one's URL therefore stays byte-identical to the one
 * this function has fetched since `B70` — no unconditional `?limit=`, no
 * default query — which is what keeps every existing caller and the assertion
 * in this module's own test untouched.
 *
 * ⚠ **A cursor is only ever the previous response's `nextCursor`.** The codec
 * is keyset over `(command_id, occurred_at)` and
 * `decodeAutomationLogCursor` answers **422** to anything it did not mint, so a
 * client that constructs one is the failure that refusal exists to catch.
 * Nothing here builds a cursor; the value is carried, opaque, from the server.
 */
export function getAutomationCommandLog(
  commandId: string,
  cursor?: string,
): Promise<AutomationCommandLogResponse> {
  const query = cursor === undefined ? '' : `?cursor=${encodeURIComponent(cursor)}`;
  return get(
    `/automations/${encodeURIComponent(commandId)}/log${query}`,
    automationCommandLogResponseSchema,
  );
}

/**
 * Fold an older page onto the page set already read — the log's twin of
 * `mergeActivityFeeds` (`lib/activity.ts`), which is the house idiom for a
 * cursor-paged feed and the reason no surface in `apps/web` uses
 * `useInfiniteQuery` (measured: **0 occurrences**).
 *
 * The merged value **is** the cache entry under
 * `queryKeys.automations.log(commandId)`, so `useAutomationCommandLog` keeps
 * returning one `AutomationCommandLogResponse` and no consumer has to flatten
 * a page array back into a response.
 *
 * ⚠ **`command`, `runState` and `retention` are kept from `current` — the
 * first (newest) page — and deliberately not taken from `next`.**
 * `retention.since` is computed as *now − windowDays* on each request, so
 * taking the newer response's copy would make the stated window drift a few
 * milliseconds every time the reader pressed "load older". That field is the
 * whole of `FR-AUTO-013`'s edge case — *"the log states the window it covers
 * rather than implying completeness"* — and a window that moves while you read
 * it states nothing. The header must not shift under a reader who is paging.
 *
 * ⚠ **`nextCursor` comes from `next`**, always: it is the only field whose
 * older value is spent.
 */
export function mergeAutomationCommandLogPages(
  current: AutomationCommandLogResponse | undefined,
  next: AutomationCommandLogResponse,
): AutomationCommandLogResponse {
  if (current === undefined) return next;
  return {
    ...current,
    entries: [...current.entries, ...next.entries],
    nextCursor: next.nextCursor,
  };
}

/**
 * Fetch the page **older** than the one in cache and extend the cached log
 * with it — the single call site a "load older" affordance needs.
 *
 * ⚠ **It does not catch.** An `ApiError` — the **422** a fabricated cursor
 * earns, or any transport failure — propagates to the caller, which must
 * render the refusal. `BoardActivitySection.loadMore()`, the precedent this
 * follows in every other respect, has a `try/finally` with **no `catch`**: copy
 * that and a refusal becomes an unhandled rejection, the spinner stops, and
 * nothing new appears. That is the *lies-rather-than-omits* shape one level up
 * from the hardcoded `nextCursor: null` this whole item exists to end, so the
 * rejection is left visible on purpose.
 *
 * ⚠ **The cursor is NOT part of the query key.** One key holds the whole
 * accumulated log; keying by cursor would give N cache entries in which "load
 * older" *replaces* the list instead of extending it — green tests, wrong
 * screen.
 *
 * Two guards on the write, both of which leave the cache untouched:
 *
 *  - **page one is gone** (`current === undefined`) — the query was evicted or
 *    invalidated mid-flight; seeding the cache with an *older* page alone would
 *    show a log that starts in the middle. Returning `undefined` from a
 *    `setQueryData` updater is TanStack's "do not update", so the pending
 *    refetch of page one wins.
 *  - **page one moved** (`current.nextCursor !== cursor`) — one of the seven
 *    mutations that invalidate `queryKeys.automations.log(...)` landed while
 *    this page was in flight, so this page is answering a question the cache no
 *    longer asks. Dropping it is correct; the reader presses again.
 */
export async function loadOlderAutomationCommandLog(
  queryClient: QueryClient,
  commandId: string,
  cursor: string,
): Promise<void> {
  const next = await getAutomationCommandLog(commandId, cursor);
  queryClient.setQueryData<AutomationCommandLogResponse>(
    queryKeys.automations.log(commandId),
    (current) => {
      if (current === undefined) return undefined;
      if (current.nextCursor !== cursor) return current;
      return mergeAutomationCommandLogPages(current, next);
    },
  );
}

export interface WorkspaceAutomationActivityFilters {
  readonly boardId?: string | null | undefined;
  readonly outcome?: AutomationRunOutcome | null | undefined;
  readonly limit?: number | undefined;
  readonly cursor?: string | undefined;
}

function workspaceAutomationActivityQuery(
  filters: WorkspaceAutomationActivityFilters,
): string {
  const params = new URLSearchParams();
  if (filters.boardId !== null && filters.boardId !== undefined) {
    params.set('boardId', filters.boardId);
  }
  if (filters.outcome !== null && filters.outcome !== undefined) {
    params.set('outcome', filters.outcome);
  }
  if (filters.limit !== undefined) {
    params.set('limit', String(filters.limit));
  }
  if (filters.cursor !== undefined) {
    params.set('cursor', filters.cursor);
  }
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

export function getWorkspaceAutomationActivity(
  workspaceId: string,
  filters: WorkspaceAutomationActivityFilters = {},
): Promise<WorkspaceAutomationActivityResponse> {
  return get(
    `/workspaces/${encodeURIComponent(workspaceId)}/automation-activity${workspaceAutomationActivityQuery(filters)}`,
    workspaceAutomationActivityResponseSchema,
  );
}

export function getWorkspaceAutomationUsage(
  workspaceId: string,
): Promise<WorkspaceAutomationUsageResponse> {
  return get(
    `/workspaces/${encodeURIComponent(workspaceId)}/automation-usage`,
    workspaceAutomationUsageResponseSchema,
  );
}

export interface WorkspaceAutomationAdminFilters {
  readonly boardId?: string | null | undefined;
}

function workspaceAutomationAdminQuery(filters: WorkspaceAutomationAdminFilters): string {
  const params = new URLSearchParams();
  if (filters.boardId !== null && filters.boardId !== undefined) {
    params.set('boardId', filters.boardId);
  }
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

export function getWorkspaceAutomationAdmin(
  workspaceId: string,
  filters: WorkspaceAutomationAdminFilters = {},
): Promise<AutomationAdminReviewResponse> {
  return get(
    `/workspaces/${encodeURIComponent(workspaceId)}/automation-admin${workspaceAutomationAdminQuery(filters)}`,
    automationAdminReviewResponseSchema,
  );
}

export function adminDisableAutomationCommand(
  commandId: string,
  body: AutomationAdminDisableBody = {},
): Promise<AutomationAdminDisableResponse> {
  return post(
    `/automations/${encodeURIComponent(commandId)}/admin-disable`,
    automationAdminDisableResponseSchema,
    automationAdminDisableBodySchema.parse(body),
  );
}

export function getCardAutomationButtons(cardId: string): Promise<AutomationButtonList> {
  return get(
    `/cards/${encodeURIComponent(cardId)}/automation-buttons`,
    automationButtonListSchema,
  );
}

export function runCardAutomationButton(
  cardId: string,
  commandId: string,
  body: AutomationButtonRunBody = { confirmDestructive: false },
): Promise<AutomationButtonRunReceipt> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/automation-buttons/${encodeURIComponent(commandId)}/run`,
    automationButtonRunReceiptSchema,
    automationButtonRunBodySchema.parse(body),
  );
}

export function getBoardAutomationButtons(
  boardId: string,
): Promise<AutomationButtonList> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/automation-buttons`,
    automationButtonListSchema,
  );
}

export function runBoardAutomationButton(
  boardId: string,
  commandId: string,
  body: AutomationButtonRunBody = { confirmDestructive: false },
): Promise<AutomationButtonRunReceipt> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/automation-buttons/${encodeURIComponent(commandId)}/run`,
    automationButtonRunReceiptSchema,
    automationButtonRunBodySchema.parse(body),
  );
}

export function createAutomationCommand(
  boardId: string,
  body: CreateAutomationCommandBody,
): Promise<AutomationCommand> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/automations`,
    automationCommandSchema,
    createAutomationCommandBodySchema.parse(body),
  );
}

export function updateAutomationCommandDefinition(
  commandId: string,
  body: UpdateAutomationDefinitionBody,
): Promise<AutomationCommand> {
  return patch(
    `/automations/${encodeURIComponent(commandId)}/definition`,
    automationCommandSchema,
    updateAutomationDefinitionBodySchema.parse(body),
  );
}

export function updateAutomationCommandEnabled(
  commandId: string,
  body: UpdateAutomationEnabledBody,
): Promise<AutomationCommandLifecycleResponse> {
  return patch(
    `/automations/${encodeURIComponent(commandId)}/enabled`,
    automationCommandLifecycleResponseSchema,
    updateAutomationEnabledBodySchema.parse(body),
  );
}

export function deleteAutomationCommand(commandId: string): Promise<null> {
  return del(`/automations/${encodeURIComponent(commandId)}`, emptyResponseSchema);
}

export function duplicateAutomationCommand(
  commandId: string,
  body: DuplicateAutomationCommandBody,
): Promise<DuplicateAutomationCommandResponse> {
  return post(
    `/automations/${encodeURIComponent(commandId)}/duplicate`,
    duplicateAutomationCommandResponseSchema,
    duplicateAutomationCommandBodySchema.parse(body),
  );
}

export function applyAutomationLifecyclePatch(
  command: AutomationCommand,
  lifecycle: AutomationCommandLifecycleResponse,
): AutomationCommand {
  return {
    ...command,
    enabled: lifecycle.enabled,
    isBroken: lifecycle.isBroken,
    visibility: lifecycle.visibility,
    updatedAt: lifecycle.updatedAt,
  };
}

function automationListKeyKind(
  queryKey: readonly unknown[],
): AutomationCommandKind | undefined {
  const commandKind = queryKey[3];
  return typeof commandKind === 'string'
    ? (commandKind as AutomationCommandKind)
    : undefined;
}

function updateBoardAutomationListQueries(
  queryClient: QueryClient,
  boardId: string,
  updater: (
    previous: AutomationCommandList | undefined,
    commandKind: AutomationCommandKind | undefined,
  ) => AutomationCommandList | undefined,
): void {
  const baseKey = queryKeys.boards.automations(boardId);
  queryClient.setQueryData<AutomationCommandList>(baseKey, (previous) =>
    updater(previous, undefined),
  );
  for (const query of queryClient.getQueryCache().findAll({ queryKey: baseKey })) {
    if (query.queryKey.length === baseKey.length) {
      continue;
    }
    queryClient.setQueryData<AutomationCommandList>(query.queryKey, (previous) =>
      updater(previous, automationListKeyKind(query.queryKey)),
    );
  }
}

function upsertAutomationCommandInList(
  previous: AutomationCommandList | undefined,
  command: AutomationCommand,
  commandKind: AutomationCommandKind | undefined,
): AutomationCommandList {
  if (commandKind !== undefined && commandKind !== command.commandKind) {
    return {
      commands: previous?.commands.filter((row) => row.id !== command.id) ?? [],
    };
  }
  return {
    commands: [
      command,
      ...(previous?.commands.filter((row) => row.id !== command.id) ?? []),
    ],
  };
}

export function useBoardAutomations(
  boardId: string,
  commandKind: AutomationCommandKind | undefined,
  enabled: boolean,
): UseQueryResult<AutomationCommandList, ApiError> {
  return useQuery({
    queryKey: queryKeys.boards.automations(boardId, commandKind),
    queryFn: () => getBoardAutomations(boardId, commandKind),
    enabled,
  });
}

export function useCardAutomationButtons(
  cardId: string,
  enabled: boolean,
): UseQueryResult<AutomationButtonList, ApiError> {
  return useQuery({
    queryKey: queryKeys.cards.automationButtons(cardId),
    queryFn: () => getCardAutomationButtons(cardId),
    enabled,
  });
}

export function useAutomationCommandLog(
  commandId: string,
  enabled: boolean,
): UseQueryResult<AutomationCommandLogResponse, ApiError> {
  return useQuery({
    queryKey: queryKeys.automations.log(commandId),
    queryFn: () => getAutomationCommandLog(commandId),
    enabled,
  });
}

export function useAutomationCommand(
  commandId: string,
  enabled: boolean,
): UseQueryResult<AutomationCommand, ApiError> {
  return useQuery({
    queryKey: queryKeys.automations.detail(commandId),
    queryFn: () => getAutomationCommand(commandId),
    enabled,
  });
}

export function useWorkspaceAutomationActivity(
  workspaceId: string,
  filters: WorkspaceAutomationActivityFilters,
  enabled: boolean,
): UseQueryResult<WorkspaceAutomationActivityResponse, ApiError> {
  const limit = filters.limit ?? 25;
  return useQuery({
    queryKey: queryKeys.workspaces.automationActivity(
      workspaceId,
      filters.boardId,
      filters.outcome,
      limit,
    ),
    queryFn: () => getWorkspaceAutomationActivity(workspaceId, { ...filters, limit }),
    enabled,
  });
}

export function useWorkspaceAutomationUsage(
  workspaceId: string,
  enabled: boolean,
): UseQueryResult<WorkspaceAutomationUsageResponse, ApiError> {
  return useQuery({
    queryKey: queryKeys.workspaces.automationUsage(workspaceId),
    queryFn: () => getWorkspaceAutomationUsage(workspaceId),
    enabled,
  });
}

export function useWorkspaceAutomationAdmin(
  workspaceId: string,
  filters: WorkspaceAutomationAdminFilters,
  enabled: boolean,
): UseQueryResult<AutomationAdminReviewResponse, ApiError> {
  return useQuery({
    queryKey: queryKeys.workspaces.automationAdmin(workspaceId, filters.boardId),
    queryFn: () => getWorkspaceAutomationAdmin(workspaceId, filters),
    enabled,
  });
}

export function useRunCardAutomationButton(): UseMutationResult<
  AutomationButtonRunReceipt,
  ApiError,
  {
    cardId: string;
    commandId: string;
    workspaceId?: string | undefined;
    confirmDestructive?: boolean;
  }
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: ({ cardId, commandId, confirmDestructive }) =>
      runCardAutomationButton(cardId, commandId, {
        confirmDestructive: confirmDestructive ?? false,
      }),
    onSuccess: (receipt, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.automationButtons(variables.cardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(variables.cardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(receipt.run.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.automations(receipt.run.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.automations.log(variables.commandId),
      });
      if (variables.workspaceId !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.workspaces.automationUsage(variables.workspaceId),
        });
      }
      announce(
        t('automationHooks.runOutcome', {
          outcome: receipt.run.outcome ?? t('automationHooks.runOutcomeFallback'),
        }),
      );
    },
    onError: () => announce(t('automationHooks.runCardButtonFailed')),
  });
}

export function useBoardAutomationButtons(
  boardId: string,
  enabled: boolean,
): UseQueryResult<AutomationButtonList, ApiError> {
  return useQuery({
    queryKey: queryKeys.boards.automationButtons(boardId),
    queryFn: () => getBoardAutomationButtons(boardId),
    enabled,
  });
}

export function useRunBoardAutomationButton(): UseMutationResult<
  AutomationButtonRunReceipt,
  ApiError,
  {
    boardId: string;
    commandId: string;
    workspaceId?: string | undefined;
    confirmDestructive?: boolean;
  }
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: ({ boardId, commandId, confirmDestructive }) =>
      runBoardAutomationButton(boardId, commandId, {
        confirmDestructive: confirmDestructive ?? false,
      }),
    onSuccess: (receipt, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.automationButtons(variables.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(variables.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.automations(variables.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.automations.log(variables.commandId),
      });
      if (variables.workspaceId !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.workspaces.automationUsage(variables.workspaceId),
        });
      }
      if (receipt.run.cardId !== null) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.cards.detail(receipt.run.cardId),
        });
      }
      announce(
        t('automationHooks.runOutcome', {
          outcome: receipt.run.outcome ?? t('automationHooks.runOutcomeFallback'),
        }),
      );
    },
    onError: () => announce(t('automationHooks.runBoardButtonFailed')),
  });
}

export function useCreateAutomationCommand(
  boardId: string,
): UseMutationResult<AutomationCommand, ApiError, CreateAutomationCommandBody> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (body) => createAutomationCommand(boardId, body),
    onSuccess: (command) => {
      updateBoardAutomationListQueries(queryClient, boardId, (previous, commandKind) =>
        upsertAutomationCommandInList(previous, command, commandKind),
      );
      queryClient.setQueryData(queryKeys.automations.detail(command.id), command);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.automations.log(command.id),
      });
    },
    onError: () => announce(t('automationHooks.createCommandFailed')),
  });
}

export function useUpdateAutomationCommandDefinition(): UseMutationResult<
  AutomationCommand,
  ApiError,
  { commandId: string; boardId: string; body: UpdateAutomationDefinitionBody }
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: ({ commandId, body }) =>
      updateAutomationCommandDefinition(commandId, body),
    onSuccess: (command, variables) => {
      updateBoardAutomationListQueries(
        queryClient,
        variables.boardId,
        (previous, commandKind) =>
          upsertAutomationCommandInList(previous, command, commandKind),
      );
      queryClient.setQueryData(queryKeys.automations.detail(command.id), command);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.automations.log(command.id),
      });
    },
    onError: () => announce(t('automationHooks.updateDefinitionFailed')),
  });
}

export function useUpdateAutomationCommandEnabled(): UseMutationResult<
  AutomationCommandLifecycleResponse,
  ApiError,
  { commandId: string; boardId: string; enabled: boolean }
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: ({ commandId, enabled }) =>
      updateAutomationCommandEnabled(commandId, { enabled }),
    onSuccess: (lifecycle, variables) => {
      updateBoardAutomationListQueries(
        queryClient,
        variables.boardId,
        (previous, commandKind) =>
          previous === undefined
            ? previous
            : {
                commands:
                  commandKind !== undefined && commandKind !== lifecycle.commandKind
                    ? previous.commands.filter((command) => command.id !== lifecycle.id)
                    : previous.commands.map((command) =>
                        command.id === lifecycle.id
                          ? applyAutomationLifecyclePatch(command, lifecycle)
                          : command,
                      ),
              },
      );
      queryClient.setQueryData<AutomationCommand>(
        queryKeys.automations.detail(lifecycle.id),
        (previous) =>
          previous === undefined
            ? previous
            : applyAutomationLifecyclePatch(previous, lifecycle),
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.automations.log(lifecycle.id),
      });
    },
    onError: () => announce(t('automationHooks.updateStateFailed')),
  });
}

export function useDeleteAutomationCommand(): UseMutationResult<
  null,
  ApiError,
  { commandId: string; boardId: string }
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: ({ commandId }) => deleteAutomationCommand(commandId),
    onSuccess: (_result, variables) => {
      updateBoardAutomationListQueries(queryClient, variables.boardId, (previous) =>
        previous === undefined
          ? previous
          : {
              commands: previous.commands.filter(
                (command) => command.id !== variables.commandId,
              ),
            },
      );
      queryClient.removeQueries({
        queryKey: queryKeys.automations.detail(variables.commandId),
      });
      queryClient.removeQueries({
        queryKey: queryKeys.automations.log(variables.commandId),
      });
    },
    onError: () => announce(t('automationHooks.deleteCommandFailed')),
  });
}

export function useDuplicateAutomationCommand(): UseMutationResult<
  DuplicateAutomationCommandResponse,
  ApiError,
  { commandId: string; body: DuplicateAutomationCommandBody }
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: ({ commandId, body }) => duplicateAutomationCommand(commandId, body),
    onSuccess: (result) => {
      updateBoardAutomationListQueries(
        queryClient,
        result.command.boardId,
        (previous, commandKind) =>
          upsertAutomationCommandInList(previous, result.command, commandKind),
      );
      queryClient.setQueryData(
        queryKeys.automations.detail(result.command.id),
        result.command,
      );
    },
    onError: () => announce(t('automationHooks.copyCommandFailed')),
  });
}

export function useAdminDisableAutomationCommand(): UseMutationResult<
  AutomationAdminDisableResponse,
  ApiError,
  { commandId: string; workspaceId: string; boardId: string; reason?: string }
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: ({ commandId, reason }) =>
      adminDisableAutomationCommand(commandId, reason === undefined ? {} : { reason }),
    onSuccess: (result, variables) => {
      queryClient.setQueryData<AutomationAdminReviewResponse>(
        queryKeys.workspaces.automationAdmin(variables.workspaceId),
        (previous) =>
          previous === undefined
            ? previous
            : {
                ...previous,
                commands: previous.commands.map((command) =>
                  command.id === result.command.id
                    ? {
                        ...command,
                        enabled: result.command.enabled,
                        isBroken: result.command.isBroken,
                        diagnostics: result.command.diagnostics,
                        visibility: result.command.visibility,
                        updatedAt: result.command.updatedAt,
                      }
                    : command,
                ),
              },
      );
      queryClient.setQueryData<AutomationAdminReviewResponse>(
        queryKeys.workspaces.automationAdmin(variables.workspaceId, variables.boardId),
        (previous) =>
          previous === undefined
            ? previous
            : {
                ...previous,
                commands: previous.commands.map((command) =>
                  command.id === result.command.id
                    ? {
                        ...command,
                        enabled: result.command.enabled,
                        isBroken: result.command.isBroken,
                        diagnostics: result.command.diagnostics,
                        visibility: result.command.visibility,
                        updatedAt: result.command.updatedAt,
                      }
                    : command,
                ),
              },
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.automations(variables.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.automations.log(variables.commandId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.automationActivity(variables.workspaceId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.automationUsage(variables.workspaceId),
      });
    },
    onError: () => announce(t('automationHooks.adminDisableFailed')),
  });
}
